import { useState, useEffect, useContext } from "react";
import { supabase } from "../supabaseClient";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import logo from "../assets/logo.png";
import "../assets/css/login.css";
import { checkPreviousSession } from "../chatSession";
import { ApiTokenContext } from "../Context/ApiTokenContext";


const initializeTheme = () => {
  const savedTheme = Cookies.get("isDarkMode");
  if (savedTheme !== undefined) {
    return savedTheme === "true";
  }
  return (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
};

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(initializeTheme());
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { setApiToken } = useContext(ApiTokenContext);

  // Device detection for store buttons
  const isApple = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isMobile = isApple || isAndroid;

  // Check for OAuth callback errors in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthError = urlParams.get("error");
    const errorMessage = urlParams.get("message");

    if (oauthError) {
      if (oauthError === "not_authorized") {
        setError(errorMessage || "Your email is not authorized to access this application.");
      } else if (oauthError === "connection_failed") {
        setError("Google sign-in failed. Please try again.");
      } else if (oauthError === "email_not_found") {
        setError("Could not retrieve email from Google. Please try again.");
      } else if (oauthError === "session_failed") {
        setError("Session creation failed. Please try again.");
      } else {
        setError(errorMessage || "Sign-in failed. Please try again.");
      }
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Auto-hide error message after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // useEffect(() => {
  //   async function checkSession() {
  //     const {
  //       data: { session },
  //     } = await supabase.auth.getSession();
  //      if (!session || !session.user) {
  //     // No valid session, stay on login
  //     return;
  //   }
  //   // Valid session, navigate to chat-bot
  //   navigate("/chat-bot");
  //   }
  //   checkSession();
  // }, [navigate]);

  // Allowed domains for email gatekeeping
  const ALLOWED_DOMAINS = ["playces.in", "integrowai.com", "fullondesigns.com", "gmail.com"];

  const isEmailAllowed = (email) => {
    if (!email) return false;
    const domain = email.toLowerCase().split("@")[1];
    return ALLOWED_DOMAINS.includes(domain);
  };

  useEffect(() => {
    async function checkSession() {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session || !session.user) {
        // No valid session, stay on login
        return;
      }

      // Extra check: Try to fetch user from Supabase
      const { data: user, error } = await supabase.auth.getUser();
      if (error || !user) {
        navigate("/login");  // Session expired or invalid, redirect to login
        return;
      }

      // Email gatekeeping - check if user's email is from allowed domain
      const userEmail = session.user.email;
      if (!isEmailAllowed(userEmail)) {
        // Sign out the user and show error
        await supabase.auth.signOut();
        setError("Your email is not authorized to access this application.");
        return;
      }

      // Set API token in context
      if (session.access_token) {
        setApiToken(session.access_token);

        // Ensure user profile exists (important for OAuth users)
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id')
            .eq('id', session.user.id)
            .single();

          if (!profile) {
            // Create profile if it doesn't exist
            await supabase
              .from('profiles')
              .insert([{
                id: session.user.id,
                email: session.user.email,
                name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0],
              }]);
          }
        } catch (err) {
          console.error("Profile creation failed:", err);
        }

        // Ensure chat session exists for the user
        try {
          const { data: chatSessions } = await supabase
            .from('chat_session')
            .select('id')
            .eq('user_auth', session.user.id)
            .eq('type', 'main')
            .order('id', { ascending: false })
            .limit(1);

          if (!chatSessions || chatSessions.length === 0) {
            // Create a new main chat session
            const { data: newSession, error: sessionError } = await supabase
              .from('chat_session')
              .insert([{ user_auth: session.user.id, type: 'main' }])
              .select();

            if (!sessionError && newSession && newSession[0]) {
              Cookies.set('chat_session_id', newSession[0].id);
            }
          } else {
            Cookies.set('chat_session_id', chatSessions[0].id);
          }
        } catch (err) {
          console.error("Chat session creation failed:", err);
        }

        // Call login_update API
        try {
          await fetch(`${process.env.REACT_APP_ZOOMEY_API_URL}login_update`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
          });
        } catch (err) {
          console.error("Login update failed:", err);
        }
      }

      navigate("/chat-bot");  // Valid session, navigate to chat-bot
    }
    checkSession();
  }, [navigate, setApiToken]);

  useEffect(() => {
    const savedTheme = Cookies.get("isDarkMode");
    if (savedTheme !== undefined) {
      setIsDarkMode(savedTheme === "true");
    }
    document.body.classList.toggle("dark-mode", savedTheme === "true");
  }, []);

  // const handleLogin = async (e) => {
  //   e.preventDefault();
  //   const { data, error } = await supabase
  //     .from("profiles")
  //     .select("email")
  //     .or(`username.eq.${email},email.eq.${email}`)
  //     .single();
  //   if (data) {
  //     const { data: authData, error: authError } =
  //       await supabase.auth.signInWithPassword({
  //         email: data.email,
  //         password: password,
  //       });
  //     if (authError) {
  //       setError(authError.message);
  //     }
  //   } else {
  //     setError("No user found for the email/username.");
  //   }
  // };
const handleLogin = async (e) => {
  e.preventDefault();
  const { data, error } = await supabase
    .from("profiles")
    .select("email")
    .or(`username.eq.${email},email.eq.${email}`)
    .single();
  if (data) {
    const { data: authData, error: authError } =
      await supabase.auth.signInWithPassword({
        email: data.email,
        password: password,
      });
    if (!authError && authData?.session?.access_token) {
      setApiToken(authData.session.access_token); // Context mein set kar

      // Abhi direct token se /login_update hit kar
      try {
        await fetch(`${process.env.REACT_APP_ZOOMEY_API_URL}login_update`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authData.session.access_token}`,
          },
          // body: JSON.stringify({ email: data.email }),
        });
      } catch (err) {
        console.error("Login update failed:", err);
      }

      // Baad mein navigate ya kuch bhi kar sakta hai
      // navigate("/chat-bot");
    } else {
      setError(authError?.message || "Login failed.");
    }
  } else {
    setError("No user found for the email/username.");
  }
};

  const handleGoogelLogin = async () => {
    try {
      setIsGoogleLoading(true);
      setError("");

      // Use Supabase's native Google OAuth
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/chat-bot`,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });

      if (oauthError) {
        console.error("OAuth Error:", oauthError);
        setError(oauthError.message || "Google sign-in failed. Please try again.");
        setIsGoogleLoading(false);
      }
      // If successful, Supabase will redirect to Google
    } catch (err) {
      console.error("Google login error:", err);
      setError("An error occurred during Google sign-in. Please try again.");
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="login-container-bg">
      {/* Hero Taglines */}
      <div className="login-hero-taglines">
        <h1 className="login-hero-headline">The perfect partner. No buts.</h1>
        <p className="login-hero-subline">A lot on your mind? Just give it to Zoomey</p>
      </div>

      <div className="login-main-wrapper">
        {/* Left Decorative Panel */}
        <div className="login-left-panel">
          {/* Floating dots animation */}
          <div className="login-floating-dots">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div className="login-left-content">
            <div className="login-left-logo">Z</div>
            <h1 className="login-left-title">Meet Zoomey</h1>
            <p className="login-left-tagline">Run your work with a lighter mind</p>
            {/* Mobile-only taglines inside left panel */}
            <div className="login-left-mobile-taglines">
              <h2 className="login-left-mobile-headline">The perfect partner. No buts.</h2>
              <p className="login-left-mobile-subline">A lot on your mind? Just give it to Zoomey</p>
            </div>
            <p className="login-left-subtitle">
              Built for freelancers and entrepreneurs to manage clients, deadlines,
              and life admin with less stress and more clarity.
            </p>

            {/* Brand Promise Section */}
            <div className="login-brand-promises">
              <div className="login-promise-item">
                <div className="login-promise-icon">
                  <i className="bi bi-lightbulb"></i>
                </div>
                <div className="login-promise-text">
                  <span className="login-promise-title">Mental space from day one</span>
                  <span className="login-promise-desc">Clear priorities without setup overload</span>
                </div>
              </div>
              
              <div className="login-promise-item">
                <div className="login-promise-icon">
                  <i className="bi bi-heart"></i>
                </div>
                <div className="login-promise-text">
                  <span className="login-promise-title">A partner that thinks ahead</span>
                  <span className="login-promise-desc">Gentle follow-ups and proactive support</span>
                </div>
              </div>
              
              <div className="login-promise-item">
                <div className="login-promise-icon">
                  <i className="bi bi-yin-yang"></i>
                </div>
                <div className="login-promise-text">
                  <span className="login-promise-title">Client work and life in one flow</span>
                  <span className="login-promise-desc">Tasks, reminders, and context in one place</span>
                </div>
              </div>

              <div className="login-promise-item">
                <div className="login-promise-icon">
                  <i className="bi bi-wind"></i>
                </div>
                <div className="login-promise-text">
                  <span className="login-promise-title">More focus, less mental overhead</span>
                  <span className="login-promise-desc">Spend energy on delivery, not juggling</span>
                </div>
              </div>

            </div>

            {/* Tagline at bottom */}
            <p className="login-left-footer">
              "From overload to clarity — one calm next step at a time"
            </p>
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="login-right-panel">
          <div className="login-card-custom login-form-animated">
            <form onSubmit={handleLogin}>
              {error && (
                <div className="login-error-alert">
                  <i className="bi bi-exclamation-circle"></i>
                  {error}
                </div>
              )}

              <div className="login-form-group">
                <label htmlFor="email">Email or Username</label>
                <input
                  type="text"
                  className="form-control login-input-custom"
                  id="email"
                  value={email}
                  placeholder="Enter your email or username"
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="login-form-group">
                <label htmlFor="password">Password</label>
                <div className="login-password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control login-input-custom"
                    id="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword(!showPassword)}
                    className="login-password-btn"
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                  </button>
                </div>
              </div>

              <div className="login-forgot-password-link">
                <Link to="/forgot-password">Forgot Password?</Link>
              </div>

              <button type="submit" className="login-btn">
                Sign In
              </button>

              <div className="login-divider">
                <div className="login-divider-line"></div>
                <span className="login-or-text">or</span>
                <div className="login-divider-line"></div>
              </div>

              <button
                type="button"
                onClick={handleGoogelLogin}
                disabled={isGoogleLoading}
                className="login-google-btn"
              >
                {isGoogleLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Signing in...
                  </>
                ) : (
                  <>
                    <svg className="login-google-icon" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>

              {/* Store buttons - shown here on desktop, in footer on mobile */}
              {!isMobile && (
                <div className="login-form-store-buttons">
                  <a
                    href={process.env.REACT_APP_GOOGLE_PLAY_URL || "#"}
                    className={`login-store-badge ${process.env.REACT_APP_GOOGLE_PLAY_URL ? "login-store-active" : ""}`}
                    title={process.env.REACT_APP_GOOGLE_PLAY_URL ? "Get it on Google Play" : "Coming Soon on Google Play"}
                    target={process.env.REACT_APP_GOOGLE_PLAY_URL ? "_blank" : undefined}
                    rel={process.env.REACT_APP_GOOGLE_PLAY_URL ? "noopener noreferrer" : undefined}
                    onClick={process.env.REACT_APP_GOOGLE_PLAY_URL ? undefined : (e) => e.preventDefault()}
                  >
                    <div className="login-store-badge-icon">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302-2.302 2.302-2.593-2.302 2.593-2.302zM5.864 2.658L16.8 8.991l-2.302 2.302-8.635-8.635z"/>
                      </svg>
                    </div>
                    <div className="login-store-badge-text">
                      <span className="login-store-badge-label">GET IT ON</span>
                      <span className="login-store-badge-name">Google Play</span>
                    </div>
                    {!process.env.REACT_APP_GOOGLE_PLAY_URL && <span className="login-store-soon">Soon</span>}
                  </a>
                  <a
                    href={process.env.REACT_APP_APP_STORE_URL || "#"}
                    className={`login-store-badge ${process.env.REACT_APP_APP_STORE_URL ? "login-store-active" : ""}`}
                    title={process.env.REACT_APP_APP_STORE_URL ? "Download on the App Store" : "Coming Soon on App Store"}
                    target={process.env.REACT_APP_APP_STORE_URL ? "_blank" : undefined}
                    rel={process.env.REACT_APP_APP_STORE_URL ? "noopener noreferrer" : undefined}
                    onClick={process.env.REACT_APP_APP_STORE_URL ? undefined : (e) => e.preventDefault()}
                  >
                    <div className="login-store-badge-icon">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                    </div>
                    <div className="login-store-badge-text">
                      <span className="login-store-badge-label">Download on the</span>
                      <span className="login-store-badge-name">App Store</span>
                    </div>
                    {!process.env.REACT_APP_APP_STORE_URL && <span className="login-store-soon">Soon</span>}
                  </a>
                </div>
              )}

            </form>
          </div>
        </div>
      </div>

      {/* Page Footer */}
      <div className="login-page-footer">
        {/* Store buttons in footer only on mobile */}
        {isMobile && (
          <div className="login-page-footer-stores">
            {isAndroid && (
              <a
                href={process.env.REACT_APP_GOOGLE_PLAY_URL || "#"}
                className={`login-store-badge ${process.env.REACT_APP_GOOGLE_PLAY_URL ? "login-store-active" : ""}`}
                title={process.env.REACT_APP_GOOGLE_PLAY_URL ? "Get it on Google Play" : "Coming Soon on Google Play"}
                target={process.env.REACT_APP_GOOGLE_PLAY_URL ? "_blank" : undefined}
                rel={process.env.REACT_APP_GOOGLE_PLAY_URL ? "noopener noreferrer" : undefined}
                onClick={process.env.REACT_APP_GOOGLE_PLAY_URL ? undefined : (e) => e.preventDefault()}
              >
                <div className="login-store-badge-icon">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302-2.302 2.302-2.593-2.302 2.593-2.302zM5.864 2.658L16.8 8.991l-2.302 2.302-8.635-8.635z"/>
                  </svg>
                </div>
                <div className="login-store-badge-text">
                  <span className="login-store-badge-label">GET IT ON</span>
                  <span className="login-store-badge-name">Google Play</span>
                </div>
                {!process.env.REACT_APP_GOOGLE_PLAY_URL && <span className="login-store-soon">Soon</span>}
              </a>
            )}
            {isApple && (
              <a
                href={process.env.REACT_APP_APP_STORE_URL || "#"}
                className={`login-store-badge ${process.env.REACT_APP_APP_STORE_URL ? "login-store-active" : ""}`}
                title={process.env.REACT_APP_APP_STORE_URL ? "Download on the App Store" : "Coming Soon on App Store"}
                target={process.env.REACT_APP_APP_STORE_URL ? "_blank" : undefined}
                rel={process.env.REACT_APP_APP_STORE_URL ? "noopener noreferrer" : undefined}
                onClick={process.env.REACT_APP_APP_STORE_URL ? undefined : (e) => e.preventDefault()}
              >
                <div className="login-store-badge-icon">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <div className="login-store-badge-text">
                  <span className="login-store-badge-label">Download on the</span>
                  <span className="login-store-badge-name">App Store</span>
                </div>
                {!process.env.REACT_APP_APP_STORE_URL && <span className="login-store-soon">Soon</span>}
              </a>
            )}
          </div>
        )}
        <span className="login-page-footer-copy">&copy; 2026 Zoomey</span>
        <span className="login-page-footer-dot">&middot;</span>
        <div className="login-page-footer-links">
          <Link to="/pricing" target="_blank" rel="noopener noreferrer">Pricing</Link>
          <span className="login-page-footer-dot">&middot;</span>
          <Link to="/faq" target="_blank" rel="noopener noreferrer">FAQ</Link>
          <span className="login-page-footer-dot">&middot;</span>
          <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</Link>
          <span className="login-page-footer-dot">&middot;</span>
          <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
